/**
 * Audit API service
 * Wraps all /audit/* endpoints.
 * Toggle USE_MOCK = false to hit the real Java backend.
 */
import httpClient from './httpClient'
import { MATCH_THRESHOLD, STATUS } from '../config/constants'

const USE_MOCK = false   // ← flip to false when backend is ready

// ─── Audit API ────────────────────────────────────────────────────────────────

/**
 * Run a policy audit comparison
 * POST /audit/run
 *
 * @param {{ policy: string, stateCode: string }} params
 * @returns {Promise<AuditResult>}
 */
export async function runAuditApi({ policy, stateCode }) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 1_200))

    // Simulate occasional API error
    if (policy.toUpperCase() === 'ERROR') {
      throw { status: 500, message: 'Rater engine unavailable. Try again later.' }
    }

    const isMatch   = Math.random() > 0.35
    const raterPrem = 1560.00
    const uiPrem    = isMatch ? 1560.00 : parseFloat((Math.random() * 400 + 1000).toFixed(2))

    return {
      success:   true,
      error:     null,
      policy,
      stateCode,
      timestamp: new Date().toISOString(),
      data: {
        raterPremium: raterPrem,
        UIPreimim:    uiPrem,
            // API typo intentionally preserved
      },
    }
  }
// const { data } = await httpClient.get(
//   `/api/socotra-wrapper/audit-policy-premium/${policy}`,
//   {
//     params: {
//       stateCode,
//     },
//   }
// )

//   return data
const { data } = await httpClient.get(
  `/api/audit-premium/audit-policy-premium/${policy}`,
)

if (data?.status === 'failed' || !data?.success) {
  throw new Error(
    data?.message ||
    data?.error   ||
    'Audit failed. Please try again.'
  )
}

return data

}

/**
 * Compute business logic from raw API response.
 * Centralised here so it's testable and not scattered across components.
 *
 * @param {object} apiResponse
 * @returns {AuditResult}
 */
export function computeAuditResult(apiResponse) {

    // Handle backend errors first
   // Handle failed backend response safely
  if (apiResponse?.status === "failed" || !apiResponse?.data) {
    return {
      ...apiResponse,
      computed: {
        raterPremium: null,
        uiPremium: null,
        difference: null,
        status: "FAILED",
      },
    }
  }

  const raterPremium = apiResponse.data?.raterPremium
  const uiPremium    = apiResponse.data?.UIPremium
  const raterFileName =apiResponse.data?.raterFileName
  const raterDriveFileId = apiResponse.data?.raterDriveFileId

  // const raterPremium = apiResponse.data.raterPremium
  // const uiPremium    = apiResponse.data.UIPremium     // API typo preserved
   const agencyName =
    apiResponse.data.agency_details?.name || ''

  const agentName =
    apiResponse.data.agency_details?.agent_name || ''
  const difference = (raterPremium != null && uiPremium != null)
  ? Math.abs(raterPremium - uiPremium)
  : null
const status = difference != null && difference <= MATCH_THRESHOLD
  ? STATUS.MATCH
  : STATUS.MISMATCH
  // const difference   = Math.abs(raterPremium - uiPremium)
  // const status       = difference <= MATCH_THRESHOLD ? STATUS.MATCH : STATUS.MISMATCH

  return {
    ...apiResponse,
    raterFileName,
    raterDriveFileId,
    computed: {
      raterPremium,
      uiPremium,
      difference,
      status,
      agencyName,
      agentName,
    },
  }
}

/**
 * Fetch audit history (placeholder — wire when backend exposes it)
 * GET /audit/history
 */
export async function getAuditHistoryApi({ page = 0, size = 20 } = {}) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 500))
    return {
      content:       MOCK_HISTORY,
      totalElements: MOCK_HISTORY.length,
      totalPages:    1,
      page,
      size,
    }
  }

  const { data } = await httpClient.get('/audit/history', { params: { page, size } })
  return data
}

// ─── Mock history data ────────────────────────────────────────────────────────
const MOCK_HISTORY = [
  { id: 'a1', policy: 'PC12345', stateCode: 'TX', status: 'MATCH',    raterPremium: 1560, uiPremium: 1560.00, difference: 0.00,  timestamp: '2025-06-01T10:22:00Z' },
  { id: 'a2', policy: 'PC98231', stateCode: 'CA', status: 'MISMATCH', raterPremium: 1560, uiPremium: 1200.00, difference: 260.0, timestamp: '2025-06-01T09:14:00Z' },
  { id: 'a3', policy: 'PC55102', stateCode: 'NY', status: 'MATCH',    raterPremium: 980,  uiPremium: 980.00,  difference: 0.00,  timestamp: '2025-06-01T08:55:00Z' },
  { id: 'a4', policy: 'PC77891', stateCode: 'FL', status: 'MATCH',    raterPremium: 1120, uiPremium: 1120.00, difference: 0.00,  timestamp: '2025-05-31T17:30:00Z' },
  { id: 'a5', policy: 'PC44302', stateCode: 'OH', status: 'MISMATCH', raterPremium: 860,  uiPremium: 720.00,  difference: 140.0, timestamp: '2025-05-31T15:10:00Z' },
]
